import { FC, useEffect } from "react";
import {
  AllotmentPolicies,
  BinInformation,
  BinSelectedInfo,
  EditAllotmentPoliciesLocationValues,
  LocationGrainTrackerValues,
  PrincipalInfoGT,
  QualityCriteriaData,
  StoragesTemplates,
} from "../../../../redux/slices/grainTracker/grainTrackerTypes";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import DoneIcon from "@mui/icons-material/Done";
import ClearIcon from "@mui/icons-material/Clear";
import { theBinIsGood } from "../../create/GrainTrackerForm";
import { SkeletonListLayout } from "../../../userLocations/locationDetailsGrainTracker/skeletenoBin";
import { LoadingButton } from "@mui/lab";
import { stylesGW } from "../../grainWiseStylesMUIComponents";

type GrainTrackerLayoutFormParams = {
  t: (arg0: string) => string;
  setLocationGrainTracker: (graintracker: any) => void;
  setInformationByClicking: (
    binInformation: any,
    principalInfoGT: PrincipalInfoGT,
    principalInfoGTCopy: PrincipalInfoGT,
    token: string,
  ) => void;
  binCreation: (token: string) => void;
  selectBinIndex: (index: number) => void;
  locationGrainTrackerValues: LocationGrainTrackerValues;
  principalInfoGT: PrincipalInfoGT;
  principalInfoGTCopy: PrincipalInfoGT;
  selectIndexLayoutBin: number | undefined;
  listOfStoragesTemplates: {
    storagesTemplates: StoragesTemplates | undefined;
    loading: boolean;
    error: string;
  };
  editBins: EditAllotmentPoliciesLocationValues;
  token: string;
  selectedBin?: {
    values: undefined | BinSelectedInfo;
    loading: boolean;
    error: string;
  };
  isBinSelected?: boolean;
  removeSelectedBin?: () => void;
  allotmentPolicies: AllotmentPolicies[];
  qualityCriteria: {
    data: QualityCriteriaData[];
    total: number;
    limit: number;
    loading: boolean;
    error: string;
  };
  selectedCriteriaToDelete: string[];
};

const GrainTrackerLayoutForm: FC<GrainTrackerLayoutFormParams> = ({
  t,
  setLocationGrainTracker,
  setInformationByClicking,
  binCreation,
  selectBinIndex,
  locationGrainTrackerValues,
  principalInfoGT,
  principalInfoGTCopy,
  selectIndexLayoutBin,
  listOfStoragesTemplates,
  editBins,
  token,
  selectedBin,
  isBinSelected,
  removeSelectedBin,
  //
  allotmentPolicies,
  qualityCriteria,
  selectedCriteriaToDelete,
}) => {
  const { storagesList, loading } = locationGrainTrackerValues;

  const characteristicsObject =
    allotmentPolicies.length >= 1
      ? allotmentPolicies?.map((values) => {
          const newObjectCharacteristics = {
            ...values,
            nameText: qualityCriteria.data.filter(
              (data) => data.id === values.storageQualityCriterionId,
            )[0]?.nameTextId?.text,

            instructionTextIdLocale: qualityCriteria.data.filter(
              (data) => data.id === values.storageQualityCriterionId,
            )[0]?.instructionTextIdLocale,

            unit: qualityCriteria.data.filter(
              (data) => data.id === values.storageQualityCriterionId,
            )[0]?.qualityCriterionUnit.SIUnit,

            symbolTextId: qualityCriteria.data.filter(
              (data) => data.id === values.storageQualityCriterionId,
            )[0]?.qualityCriterionUnit.symbolTextId.text,

            descriptionTextId: qualityCriteria.data.filter(
              (data) => data.id === values.storageQualityCriterionId,
            )[0]?.qualityCriterionUnit.descriptionTextId.text,
          };

          return newObjectCharacteristics;
        })
      : [];

  const finalValues = characteristicsObject?.filter(
    (val) => !selectedCriteriaToDelete?.find((s) => s === val?.id),
  );

  const handleListItemClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    index: number,
    binInformation: any,
  ) => {
    setLocationGrainTracker({
      ...locationGrainTrackerValues,
      isEditingBin: true,
    });
    selectBinIndex(index);
    setInformationByClicking(
      binInformation,
      principalInfoGT,
      principalInfoGTCopy,
      token,
    );
  };

  const setToTrueEditAddStorage = () => {
    binCreation(token);
    selectBinIndex(storagesList.length);
  };

  const sorted = storagesList?.toSorted(
    (a: any, b: any) =>
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

  const findTheSelectedBinIndex = () => {
    if (isBinSelected && selectedBin?.values) {
      const foundIndex = sorted.findIndex(
        (bin) => bin.id === selectedBin.values?.id,
      );

      return foundIndex !== -1;
    }
  };

  const thePoliciesAreNotEqual = (): boolean => {
    const originalIds = (principalInfoGTCopy?.allotmentPolicies ?? [])
      .map((p) => p.id)
      .sort()
      .join(",");
    const currentIds = finalValues
      .map((p) => p.id)
      .sort()
      .join(",");
    return originalIds !== currentIds;
  };

  const theStorageInfoIsNotEqual = (): boolean => {
    return (
      JSON.stringify(principalInfoGT) !== JSON.stringify(principalInfoGTCopy)
    );
  };

  useEffect(() => {
    if (selectedBin?.values && isBinSelected) {
      handleListItemClick(
        {} as React.MouseEvent<HTMLDivElement, MouseEvent>,
        findTheSelectedBinIndex()
          ? (sorted.findIndex(
              (bin) => bin.id === selectedBin?.values?.id,
            ) as number)
          : 0,
        findTheSelectedBinIndex()
          ? (sorted.find(
              (bin) => bin.id === selectedBin?.values?.id,
            ) as BinInformation)
          : (sorted[0] as BinInformation),
      );
      setTimeout(() => {
        removeSelectedBin?.();
      }, 300);
    }
  }, []);

  return (
    <div className="layout-graintracker-container">
      <div className="layout-new-space">
        {t("Grain_tracker.new_storage_desc")}
      </div>
      <div className="divider-graintracker-layout" />

      <Box sx={stylesGW.GrainTrackerLayoutForm.layoutGraintrackerContainerBox}>
        {!loading ? (
          <List component="nav">
            {storagesList.length >= 1
              ? sorted.map((bins: BinInformation, indx) => {
                  return (
                    <ListItemButton
                      selected={selectIndexLayoutBin === indx}
                      key={bins.id}
                      disabled={
                        theStorageInfoIsNotEqual() || thePoliciesAreNotEqual()
                      }
                      onClick={(event) =>
                        handleListItemClick(event, indx, bins)
                      }
                    >
                      <ListItemText primary={bins.name} />
                      {!theBinIsGood(
                        bins.cerealTypeId,
                        bins.storageTemplateId,
                        bins.characteristics?.capacityMetricTonsWheatEq,
                      ) ? (
                        <ListItemIcon>
                          <DoneIcon />
                        </ListItemIcon>
                      ) : (
                        <ListItemIcon>
                          <ClearIcon />
                        </ListItemIcon>
                      )}
                    </ListItemButton>
                  );
                })
              : ""}
          </List>
        ) : (
          <>
            <List sx={stylesGW.GrainTrackerLayoutForm.list}>
              {SkeletonListLayout()}
            </List>
            <List sx={stylesGW.GrainTrackerLayoutForm.list}>
              {SkeletonListLayout()}
            </List>
          </>
        )}
        <LoadingButton
          loading={loading || editBins.editedLoading}
          sx={stylesGW.GrainTrackerLayoutForm.loadingButton}
          size="large"
          startIcon={<AddIcon />}
          disabled={
            theStorageInfoIsNotEqual() || thePoliciesAreNotEqual() || loading
          }
          onClick={setToTrueEditAddStorage}
        >
          {t("Grain_tracker.desc_add_storage_button")}
        </LoadingButton>
      </Box>
    </div>
  );
};

export default GrainTrackerLayoutForm;
